// components/canvas-artifact-preview.js — POLISHED ARTIFACT PREVIEW
// ═══════════════════════════════════════════════════════════
// Step A of the canvas overhaul. A single component that takes an
// artifact + its kind and renders it beautifully:
//
//   doc    → styled markdown (headings, bold, italic, lists, code,
//            links, paragraph breaks). Tiny inline renderer — no
//            external dependency, ~100 lines.
//   code   → line-numbered, monospace, language-tagged code block
//            with subtle background tint and a copy button
//   html   → sandboxed iframe (delegated, already in step E)
//   image  → polished image with click-to-fullscreen
//   link   → URL card with sage hover
//   unknown → quiet "no preview" message
//
// Used by the canvas preview overlay AND (later) by step B's
// editable doc mode as the read-mode rendering layer.
//
// Why a dedicated component instead of reusing MessageContent: that
// component is coupled to message context (sender, role, hover,
// reaction bar, edit). Artifacts need a cleaner surface that the
// editable canvas modes can build on.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { getMode, isEditable } from '../lib/canvas-modes';

export default function CanvasArtifactPreview({ artifact, node, kind, mode = 'read', supabase, userRecord }) {
  if (!artifact) {
    return <NoPreview message="Preview unavailable. The artifact for this file could not be loaded." />;
  }

  // Edit mode dispatches to the registered editor for this kind.
  // Falls back to read mode if the kind has no editor (defensive —
  // the toggle won't render in the parent in that case anyway).
  if (mode === 'edit' && isEditable(kind)) {
    const Editor = getMode(kind).editor;
    if (Editor) return <Editor artifact={artifact} node={node} supabase={supabase} userRecord={userRecord} />;
  }

  // Read mode — universal, every kind has a read view.
  // Wrapper ensures flex: 1 propagates to fill the canvas panel.
  const content = (() => {
    if (kind === 'image') return <ImagePreview src={artifact.content} alt={node?.title || ''} />;
    if (kind === 'svg') return <SvgPreview content={artifact.content} />;
    if (kind === 'html') return <HtmlPreview content={artifact.content} title={node?.title || 'HTML preview'} />;
    if (kind === 'code') return <CodePreview content={artifact.content || ''} language={artifact.artifact_type} />;
    if (kind === 'link') return <LinkPreview url={artifact.content} description={artifact.description} />;
    if (kind === 'doc') return <MarkdownPreview content={artifact.content || ''} />;
    return <NoPreview message="No preview for this artifact type." />;
  })();

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {content}
    </div>
  );
}

// ── Image preview with click-to-fullscreen ─────────────────
function ImagePreview({ src, alt }) {
  const [fullscreen, setFullscreen] = useState(false);
  return (
    <>
      <div style={{ padding: 18, textAlign: 'center', background: '#07070a' }}>
        <img
          src={src}
          alt={alt}
          onClick={() => setFullscreen(true)}
          style={{
            display: 'inline-block',
            maxWidth: '100%',
            maxHeight: '70vh',
            objectFit: 'contain',
            cursor: 'zoom-in',
            borderRadius: 4,
            boxShadow: '0 4px 18px rgba(0,0,0,0.4)',
          }}
        />
      </div>
      {fullscreen && (
        <div
          onClick={() => setFullscreen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 20,
          }}
        >
          <img
            src={src}
            alt={alt}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </div>
      )}
    </>
  );
}

// ── HTML preview (sandboxed iframe) ────────────────────────
// Fills the full available height when rendered in Focus Mode (flex
// parent), falls back to 70vh in other contexts. allow-scripts
// enables Chart.js, D3, and other interactive content Shift generates.
function HtmlPreview({ content, title }) {
  return (
    <iframe
      srcDoc={content || ''}
      sandbox="allow-scripts"
      title={title}
      style={{
        display: 'block',
        width: '100%',
        flex: 1,
        minHeight: 0,
        border: 'none',
        background: '#fff',
      }}
    />
  );
}

// ── SVG preview — rendered inline with zoom ───────��───────
// Renders inline SVG content directly (not as <img>) so CSS styling
// and animations work. Includes a dark background and centers the SVG.
function SvgPreview({ content }) {
  const [fullscreen, setFullscreen] = useState(false);
  return (
    <>
      <div
        onClick={() => setFullscreen(true)}
        style={{
          padding: 24,
          background: '#07070a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'zoom-in',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
        dangerouslySetInnerHTML={{ __html: content || '' }}
      />
      {fullscreen && (
        <div
          onClick={() => setFullscreen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 40,
          }}
          dangerouslySetInnerHTML={{ __html: content || '' }}
        />
      )}
    </>
  );
}

// ── Code preview with line numbers + copy button ───────────
function CodePreview({ content, language }) {
  const [copied, setCopied] = useState(false);
  const lines = (content || '').split('\n');
  const lang = (language || '').toLowerCase();

  function copy() {
    try {
      navigator.clipboard.writeText(content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) { /* clipboard blocked */ }
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Language tag + copy button row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderBottom: '1px solid #1e1e2a',
        background: '#07070a',
      }}>
        <span style={{
          fontSize: 9,
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          color: '#7a9e82',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          {lang || 'Code'}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={copy}
          style={{
            background: copied ? 'rgba(122,158,130,0.16)' : 'transparent',
            border: `1px solid ${copied ? '#7a9e82' : '#2a2a2a'}`,
            borderRadius: 4,
            color: copied ? '#7a9e82' : '#666',
            fontSize: 9,
            fontFamily: 'Georgia, serif',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '3px 8px',
            cursor: 'pointer',
            transition: 'all 0.12s',
          }}
          onMouseEnter={e => { if (!copied) e.currentTarget.style.color = '#7a9e82'; }}
          onMouseLeave={e => { if (!copied) e.currentTarget.style.color = '#666'; }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Code body — line numbers gutter + content */}
      <div style={{
        display: 'flex',
        background: '#07070a',
        fontFamily: '"SF Mono", "Courier New", monospace',
        fontSize: 12,
        lineHeight: 1.7,
      }}>
        {/* Gutter — line numbers, right-aligned, muted */}
        <div style={{
          padding: '14px 10px 14px 14px',
          textAlign: 'right',
          color: '#3a3a3a',
          userSelect: 'none',
          borderRight: '1px solid #161616',
          background: '#0c0c0c',
          flexShrink: 0,
        }}>
          {lines.map((_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        {/* Content */}
        <pre style={{
          margin: 0,
          padding: '14px 16px',
          color: '#d4d4d4',
          whiteSpace: 'pre',
          overflowX: 'auto',
          flex: 1,
        }}>
          {content || '(No content)'}
        </pre>
      </div>
    </div>
  );
}

// ── Link preview ───────────────────────────────────────────
function LinkPreview({ url, description }) {
  return (
    <div style={{ padding: 24, fontFamily: 'Georgia, serif' }}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#7a9e82',
          fontSize: 13,
          wordBreak: 'break-all',
          textDecoration: 'none',
          borderBottom: '1px solid rgba(122,158,130,0.3)',
          paddingBottom: 1,
          transition: 'border-color 0.12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#7a9e82'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(122,158,130,0.3)'; }}
      >
        {url}
      </a>
      {description && (
        <div style={{ marginTop: 14, color: '#888', fontSize: 12, lineHeight: 1.7 }}>
          {description}
        </div>
      )}
    </div>
  );
}

// ── Markdown preview — tiny inline renderer ────────────────
// Handles the most common markdown features without a dependency:
//   # ## ###       → headings
//   **text**       → bold
//   *text* / _t_   → italic
//   `code`         → inline code
//   ```fenced```   → code block (rendered via CodePreview for parity)
//   - item, 1.     → unordered / ordered list
//   [text](url)    → link
//   ---            → horizontal rule
//   blank line     → paragraph break
//
// Not a full CommonMark — escape sequences, nested lists, tables, and
// images aren't handled. Good enough for typical Shift artifact prose.
function MarkdownPreview({ content }) {
  const blocks = parseMarkdownBlocks(content);
  return (
    <div style={{
      padding: '24px 28px',
      color: '#d4d4d4',
      fontFamily: 'Georgia, serif',
      fontSize: 14,
      lineHeight: 1.75,
      flex: 1,
      overflowY: 'auto',
      minHeight: 0,
    }}>
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

// Parse markdown into a flat array of block objects.
function parseMarkdownBlocks(content) {
  const lines = (content || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ type: 'code', lang, content: codeLines.join('\n') });
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      blocks.push({ type: 'heading', level: headingMatch[1].length, content: headingMatch[2] });
      i++;
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}\s*$/.test(line)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    // List (collect contiguous list lines)
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const items = [];
      const ordered = /^\s*\d+\.\s+/.test(line);
      while (i < lines.length && (/^\s*[-*]\s+/.test(lines[i]) || /^\s*\d+\.\s+/.test(lines[i]))) {
        items.push(lines[i].replace(/^\s*(?:[-*]|\d+\.)\s+/, ''));
        i++;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    // Blank line — paragraph separator
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph (collect until blank line or block boundary)
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('```') &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^[-*_]{3,}\s*$/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', content: paraLines.join(' ') });
    }
  }
  return blocks;
}

function renderBlock(block, key) {
  if (block.type === 'heading') {
    const sizes = { 1: 24, 2: 20, 3: 17, 4: 15, 5: 14, 6: 13 };
    const margins = { 1: '24px 0 16px', 2: '22px 0 14px', 3: '20px 0 12px', 4: '18px 0 10px', 5: '16px 0 10px', 6: '14px 0 10px' };
    return (
      <div
        key={key}
        style={{
          fontSize: sizes[block.level] || 14,
          fontWeight: 700,
          color: '#e8e8e8',
          margin: margins[block.level] || '14px 0 10px',
          lineHeight: 1.35,
        }}
      >
        {renderInline(block.content)}
      </div>
    );
  }
  if (block.type === 'paragraph') {
    return (
      <p key={key} style={{ margin: '0 0 14px' }}>
        {renderInline(block.content)}
      </p>
    );
  }
  if (block.type === 'list') {
    const Tag = block.ordered ? 'ol' : 'ul';
    return (
      <Tag key={key} style={{ margin: '0 0 14px', paddingLeft: 24 }}>
        {block.items.map((item, idx) => (
          <li key={idx} style={{ marginBottom: 6 }}>
            {renderInline(item)}
          </li>
        ))}
      </Tag>
    );
  }
  if (block.type === 'hr') {
    return <hr key={key} style={{ border: 'none', borderTop: '1px solid #272736', margin: '20px 0' }} />;
  }
  if (block.type === 'code') {
    return (
      <div key={key} style={{ margin: '14px 0', borderRadius: 6, overflow: 'hidden' }}>
        <CodePreview content={block.content} language={block.lang} />
      </div>
    );
  }
  return null;
}

// Render inline markdown — bold, italic, code, links — into React
// elements. Works by repeatedly finding the next inline pattern,
// splitting the text into a stream of plain text + element nodes.
function renderInline(text) {
  if (!text) return null;
  const out = [];
  let remaining = text;
  let key = 0;

  // Patterns in priority order. Bold (**) and italic (* / _) overlap,
  // so bold is checked first. Inline code wins over both.
  const patterns = [
    { re: /`([^`]+)`/, type: 'code' },
    { re: /\*\*([^*]+)\*\*/, type: 'bold' },
    { re: /\*([^*\n]+)\*/, type: 'italic' },
    { re: /_([^_\n]+)_/, type: 'italic' },
    { re: /\[([^\]]+)\]\(([^)]+)\)/, type: 'link' },
  ];

  while (remaining.length > 0) {
    let earliest = null;
    for (const p of patterns) {
      const m = remaining.match(p.re);
      if (m && (earliest === null || m.index < earliest.match.index)) {
        earliest = { match: m, pattern: p };
      }
    }
    if (!earliest) {
      out.push(remaining);
      break;
    }
    const { match, pattern } = earliest;
    if (match.index > 0) out.push(remaining.slice(0, match.index));
    if (pattern.type === 'code') {
      out.push(
        <code
          key={key++}
          style={{
            background: '#1e1e2a',
            border: '1px solid #2a2a2a',
            borderRadius: 3,
            padding: '1px 5px',
            fontFamily: '"SF Mono", "Courier New", monospace',
            fontSize: '0.92em',
            color: '#c8a96e',
          }}
        >
          {match[1]}
        </code>
      );
    } else if (pattern.type === 'bold') {
      out.push(<strong key={key++} style={{ color: '#e8e8e8' }}>{match[1]}</strong>);
    } else if (pattern.type === 'italic') {
      out.push(<em key={key++}>{match[1]}</em>);
    } else if (pattern.type === 'link') {
      out.push(
        <a
          key={key++}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#7a9e82', textDecoration: 'none', borderBottom: '1px solid rgba(122,158,130,0.3)' }}
        >
          {match[1]}
        </a>
      );
    }
    remaining = remaining.slice(match.index + match[0].length);
  }
  return out;
}

// ── No preview fallback ────────────────────────────────────
function NoPreview({ message }) {
  return (
    <div style={{
      padding: 40,
      color: '#555',
      fontSize: 12,
      fontFamily: 'Georgia, serif',
      fontStyle: 'italic',
      textAlign: 'center',
    }}>
      {message}
    </div>
  );
}
